import { Alert, ProgressBarOverlay, Typography } from '@compass/components'
import { Paper } from '@compass/Paper'
import { cn } from '@compass/utils'
import type { Route as BotDetectionEventsRoute } from '@routes/_authenticated/_layout/workspaces/$subscriptionId/bot-detection/events/'
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Row } from '@tanstack/react-table'
import { AppRoute, buildRoute } from 'appRoutes'
import { EventsTable } from 'components/Table/EventsTable'
import { LimitSelector } from 'components/Table/LimitSelector'
import { TablePagination } from 'components/Table/Table'
import { TableColumnSelector } from 'components/Table/TableColumnSelector'
import { GenericError } from 'const'
import { GenericEmptyState } from 'features/commonUI'
import { TOTAL_HITS_MAX_LIMIT } from 'hooks/events'
import { usePaginationForQuery } from 'hooks/usePaginationForQuery'
import { SearchX, X } from 'lucide-react'
import { useMemo } from 'react'

import { BotDetectionEvent, BotDetectionEventsListResponse } from '../../models'
import { CreateBotDetectionCellFilter } from './types'
import { useBotDetectionEventsTable } from './useBotDetectionEventsTable'
import { formatCappedCount } from './utils'

const botDetectionEventsPath = `${AppRoute.BotDetectionEvents}/` satisfies typeof BotDetectionEventsRoute.fullPath

function formatTotalCount(count: number) {
  // totalHits is clamped at TOTAL_HITS_MAX_LIMIT upstream, so hitting the cap means "this many or more".
  return `${formatCappedCount(count, count >= TOTAL_HITS_MAX_LIMIT)} ${count === 1 ? 'event' : 'events'}`
}

export function BotDetectionEventsTable({
  subscriptionId,
  query,
  limit,
  createCellFilter,
  appliedFilterCount = 0,
  totalCount,
  onClear,
}: {
  subscriptionId: string
  query: UseInfiniteQueryResult<InfiniteData<BotDetectionEventsListResponse>, GenericError>
  limit: number
  createCellFilter: CreateBotDetectionCellFilter
  appliedFilterCount?: number
  totalCount?: number
  onClear: () => void
}) {
  const navigate = useNavigate({ from: botDetectionEventsPath })
  const { data, isFetching, isError, isLoading } = query
  const {
    values: currentPageEvents,
    hasPreviousEntries,
    goToPreviousPage,
    hasNextEntries,
    goToNextPage,
  } = usePaginationForQuery(query, (page) => page.events)
  const events = currentPageEvents ?? []
  const showSkeleton = isLoading && !data
  const { table, columnVisibility, setColumnVisibility, columnOrder, setColumnOrder, disabledByFeatureFlag } =
    useBotDetectionEventsTable({ subscriptionId, events, showSkeleton, limit, createCellFilter })

  const tableDependencies = useMemo(() => [columnVisibility, columnOrder], [columnVisibility, columnOrder])

  return (
    <Paper className='p-0 overflow-hidden relative' padded={false}>
      {isFetching && <ProgressBarOverlay data-testid='bot-detection-events-table-loader' />}
      <div className='overflow-x-auto overscroll-x-none'>
        <div className='sticky left-0 flex gap-3 justify-between items-center px-4 py-3 border-b text-left'>
          {totalCount !== undefined ? (
            <Typography weight='medium' data-testid='bot-detection-events-total-count'>
              {formatTotalCount(totalCount)}
            </Typography>
          ) : (
            <span />
          )}
          <TableColumnSelector
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
            columns={table.getAllLeafColumns().filter((column) => !disabledByFeatureFlag.has(column.id))}
          />
        </div>

        <EventsTable
          table={table}
          ariaLabel='Bot detection events table'
          className='relative'
          size='lg'
          dependencies={tableDependencies}
          rows={isError ? [] : table.getRowModel().rows}
          renderEmptyState={() =>
            isError ? (
              <Alert variant='error' className='m-2'>
                Unable to load bot events.
              </Alert>
            ) : (
              <GenericEmptyState
                icon={<SearchX />}
                title='No events were found'
                description={
                  appliedFilterCount > 0
                    ? 'Try expanding the date range or adjusting any filters.'
                    : 'Try adjusting the date range.'
                }
                buttonProps={
                  appliedFilterCount > 0
                    ? {
                        onPress: onClear,
                        variant: 'secondary',
                        children: (
                          <>
                            <X /> Clear filters
                          </>
                        ),
                      }
                    : undefined
                }
              />
            )
          }
          rowProps={(row: Row<BotDetectionEvent>) => {
            const event = row.original
            const route = event.event_id
              ? buildRoute(AppRoute.BotDetectionEventDetails, {
                  subscriptionId,
                  eventId: event.event_id,
                })
              : undefined

            return {
              id: row.id,
              className: 'border-t relative',
              href: route,
              dataTestId: 'bot-detection-event-row',
            }
          }}
        />

        {(events.length > 0 || hasNextEntries || hasPreviousEntries) && (
          <div className='sticky left-0 flex justify-between gap-4 h-12 px-4 border-t'>
            <div className='flex items-center gap-2'>
              <Typography size='sm' className='text-gray-800'>
                Show
              </Typography>
              <LimitSelector
                onLimitChange={(nextLimit) => navigate({ to: '.', search: (old) => ({ ...old, limit: nextLimit }) })}
              />
            </div>
            <TablePagination
              hasNextEntries={hasNextEntries}
              hasPreviousEntries={hasPreviousEntries}
              onRequestNextEntries={goToNextPage}
              onRequestPreviousEntries={goToPreviousPage}
              isLoading={isFetching}
              className={cn('m-0 p-0 gap-0 items-center')}
            />
          </div>
        )}
      </div>
    </Paper>
  )
}
